import { useMemo } from 'react';

import Modal from '@/components/ui/Modal';
import { cn } from '@/components/ui/cn';

/** Stable "latest" filenames the release script publishes next to the versioned artifacts + updater
 *  manifest (see desktop/README.md). Direct-download links stay valid across versions. */
const MAC_FILE = 'SparkBoards-macOS.dmg';
const WIN_FILE = 'SparkBoards-Windows-setup.exe';

type OS = 'mac' | 'windows' | 'linux' | 'other';

/** Best-effort OS sniff to recommend the right build first (falls back to showing both). */
function detectOS(): OS {
  if (typeof navigator === 'undefined') return 'other';
  const uaData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
  const s = `${uaData?.platform ?? ''} ${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`.toLowerCase();
  if (/mac|iphone|ipad|ipod/.test(s)) return 'mac';
  if (/win/.test(s)) return 'windows';
  if (/linux|x11|ubuntu|android|cros/.test(s)) return 'linux';
  return 'other';
}

const AppleGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M16.36 12.66c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.71-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.18-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.74 2.21 1.1-.04 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.15.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.29-.88-2.31-3.49zM14.2 6.02c.6-.73 1.01-1.75.9-2.77-.87.04-1.92.58-2.54 1.31-.56.64-1.05 1.68-.92 2.67.97.08 1.96-.49 2.56-1.21z" />
  </svg>
);
const WindowsGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M3 5.7 10.4 4.7v6.6H3V5.7zm0 12.6 7.4 1v-6.5H3v5.5zm8.3 1.14L21 20.8v-8.5h-9.7v6.64zM11.3 4.55 21 3.2v8.5h-9.7V4.55z" />
  </svg>
);

function DownloadButton({ os, href, primary }: { os: 'mac' | 'windows'; href?: string; primary: boolean }) {
  const disabled = !href;
  return (
    <a
      href={href}
      aria-disabled={disabled}
      onClick={(e) => { if (disabled) e.preventDefault(); }}
      className={cn(
        'flex items-center justify-center gap-2.5 rounded-xl px-4 py-3 text-[13.5px] font-semibold transition-colors',
        primary ? 'bg-accent text-ink hover:bg-accent/90' : 'border border-line text-fg-soft hover:bg-layer',
        disabled && 'pointer-events-none opacity-40',
      )}
    >
      {os === 'mac' ? <AppleGlyph /> : <WindowsGlyph />}
      Download for {os === 'mac' ? 'macOS' : 'Windows'}
    </a>
  );
}

/** "Get desktop app" (SBB-16): a self-hosted Tauri build of Spark Boards. Recommends the build for the
 *  visitor's OS and links both installers (served from the VPS). The app auto-updates and stays current
 *  with the web app automatically (it loads the same hosted site). */
export default function GetDesktopAppModal({ open, onClose, returnFocusTo }: { open: boolean; onClose: () => void; returnFocusTo?: { readonly current: HTMLElement | null } }) {
  const os = useMemo(detectOS, []);
  const base = (typeof window !== 'undefined' && window.config?.desktopDownloadBase) || '';
  const macHref = base ? `${base}/${MAC_FILE}` : undefined;
  const winHref = base ? `${base}/${WIN_FILE}` : undefined;
  const macFirst = os !== 'windows'; // default to macOS-first unless we clearly detect Windows

  return (
    <Modal open={open} onClose={onClose} title="Get the desktop app" returnFocusTo={returnFocusTo} className="max-w-[460px]">
      <div className="space-y-4 p-1">
        <p className="text-[13px] leading-relaxed text-fg-soft">
          A native desktop app for {os === 'windows' ? 'Windows' : os === 'mac' ? 'macOS' : 'macOS and Windows'} — with
          multiple <span className="font-medium text-fg">tabs</span> (like Miro & Figma), a dock/taskbar icon, and{' '}
          <span className="font-medium text-fg">automatic updates</span>. It stays in lock-step with the web app, so new
          features arrive without re-downloading.
        </p>

        <div className="grid gap-2.5">
          {macFirst ? (
            <>
              <DownloadButton os="mac" href={macHref} primary />
              <DownloadButton os="windows" href={winHref} primary={false} />
            </>
          ) : (
            <>
              <DownloadButton os="windows" href={winHref} primary />
              <DownloadButton os="mac" href={macHref} primary={false} />
            </>
          )}
        </div>

        {!base ? (
          <p className="rounded-lg bg-layer px-3 py-2 text-[11.5px] text-fg-faint">
            Desktop downloads aren’t available in this environment.
          </p>
        ) : (
          <div className="rounded-lg bg-layer px-3 py-2 text-[11.5px] leading-relaxed text-fg-faint">
            {os === 'mac' ? (
              <>
                <span className="font-medium text-fg-soft">First open on macOS:</span> the app isn’t Apple-notarized
                yet, so <span className="font-medium text-fg-soft">Control-click the app → Open → Open</span> (once).
                If it’s still blocked, run in Terminal:{' '}
                <code className="rounded bg-surface-inset px-1 py-0.5 text-[10.5px] text-fg-soft">xattr -dr com.apple.quarantine "/Applications/Spark Boards.app"</code>
              </>
            ) : os === 'windows' ? (
              <>
                <span className="font-medium text-fg-soft">First open on Windows:</span> SmartScreen may prompt —
                choose <span className="font-medium text-fg-soft">More info → Run anyway</span>.
              </>
            ) : (
              'Choose the installer for your operating system.'
            )}{' '}
            Opening a boards.uxmy.tech link hands off to the app once it’s installed.
          </div>
        )}
      </div>
    </Modal>
  );
}
